/**
 * Analytics Docs Page
 *
 * Admin-only page for viewing analytics documentation from .docs/analytics/
 * Features:
 * - Sidebar navigation organized by category
 * - Full-text search across all documents
 * - Markdown rendering with syntax highlighting
 * - Responsive layout
 */

import React, { useState, useMemo, useCallback } from 'react';
import ReactMarkdown from 'react-markdown';
import {
  Box,
  TextField,
  InputAdornment,
  Typography,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Chip,
  Paper,
  Divider,
  useTheme,
  alpha,
  IconButton,
  Collapse,
  Tooltip,
} from '@mui/material';
import {
  Search as SearchIcon,
  Article as ArticleIcon,
  Assessment as AssessmentIcon,
  Analytics as AnalyticsIcon,
  Clear as ClearIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Description as DescriptionIcon,
  Code as CodeIcon,
  LocalOffer as TagIcon,
} from '@mui/icons-material';
import { analyticsDocs, type AnalyticsDoc } from 'virtual:analytics-docs';

// Map icon names to MUI icons
const iconMap: Record<string, React.ElementType> = {
  assessment: AssessmentIcon,
  analytics: AnalyticsIcon,
  article: ArticleIcon,
  description: DescriptionIcon,
  code: CodeIcon,
};

function getIcon(iconName: string): React.ElementType {
  return iconMap[iconName] || ArticleIcon;
}

// Group documents by category
function groupByCategory(docs: AnalyticsDoc[]): Map<string, AnalyticsDoc[]> {
  const groups = new Map<string, AnalyticsDoc[]>();
  docs.forEach(doc => {
    const existing = groups.get(doc.category) || [];
    groups.set(doc.category, [...existing, doc]);
  });
  return groups;
}

// Search through documents
function searchDocs(docs: AnalyticsDoc[], query: string): AnalyticsDoc[] {
  if (!query.trim()) return docs;

  const lowerQuery = query.toLowerCase();
  return docs.filter(doc => {
    // Search in title, description, tags, and content
    return (
      doc.title.toLowerCase().includes(lowerQuery) ||
      doc.description.toLowerCase().includes(lowerQuery) ||
      doc.tags.some(tag => tag.toLowerCase().includes(lowerQuery)) ||
      doc.content.toLowerCase().includes(lowerQuery)
    );
  });
}

// Highlight search matches in content
function highlightMatches(content: string, query: string): string {
  if (!query.trim()) return content;

  // Create a regex that matches the query (case insensitive)
  const regex = new RegExp(`(${query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
  return content.replace(regex, '**$1**');
}

export function AnalyticsDocs() {
  const theme = useTheme();
  const [selectedDocId, setSelectedDocId] = useState<string>(analyticsDocs[0]?.id || '');
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(
    new Set(Array.from(groupByCategory(analyticsDocs).keys())),
  );

  // Filter documents based on search
  const filteredDocs = useMemo(() => {
    return searchDocs(analyticsDocs, searchQuery);
  }, [searchQuery]);

  // Group filtered documents by category
  const groupedDocs = useMemo(() => {
    return groupByCategory(filteredDocs);
  }, [filteredDocs]);

  // Get selected document
  const selectedDoc = useMemo(() => {
    return analyticsDocs.find(doc => doc.id === selectedDocId);
  }, [selectedDocId]);

  // Handle category toggle
  const toggleCategory = useCallback((category: string) => {
    setExpandedCategories(prev => {
      const next = new Set(prev);
      if (next.has(category)) {
        next.delete(category);
      } else {
        next.add(category);
      }
      return next;
    });
  }, []);

  // Handle search clear
  const clearSearch = useCallback(() => {
    setSearchQuery('');
  }, []);

  // Count matches in content
  const countMatches = useCallback(
    (content: string): number => {
      if (!searchQuery.trim()) return 0;
      const regex = new RegExp(searchQuery.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'gi');
      return (content.match(regex) || []).length;
    },
    [searchQuery],
  );

  if (analyticsDocs.length === 0) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <DescriptionIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
        <Typography variant="h6" color="text.secondary">
          No analytics documentation found
        </Typography>
        <Typography variant="body2" color="text.disabled">
          Add markdown files to .docs/analytics/ to see them here
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', height: 'calc(100vh - 64px)', overflow: 'hidden' }}>
      {/* Left Sidebar - Document Navigation */}
      <Box
        sx={{
          width: 320,
          flexShrink: 0,
          borderRight: `1px solid ${theme.palette.divider}`,
          display: 'flex',
          flexDirection: 'column',
          bgcolor: theme.palette.grey[50],
        }}
      >
        {/* Header */}
        <Box sx={{ p: 2, borderBottom: `1px solid ${theme.palette.divider}` }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 40,
                height: 40,
                borderRadius: 1.5,
                bgcolor: alpha(theme.palette.primary.main, 0.1),
              }}
            >
              <AnalyticsIcon sx={{ color: theme.palette.primary.main }} />
            </Box>
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
                Analytics Docs
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {analyticsDocs.length} document{analyticsDocs.length !== 1 ? 's' : ''}
              </Typography>
            </Box>
          </Box>

          {/* Search */}
          <TextField
            size="small"
            fullWidth
            placeholder="Search events, tags..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            sx={{
              '& .MuiOutlinedInput-root': {
                bgcolor: 'white',
              },
            }}
           slotProps={{ input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" color="action" />
                </InputAdornment>
              ),
              endAdornment: searchQuery && (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={clearSearch}>
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ),
            } }}/>

          {searchQuery && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              {filteredDocs.length} result{filteredDocs.length !== 1 ? 's' : ''} found
            </Typography>
          )}
        </Box>

        {/* Document List */}
        <Box sx={{ flex: 1, overflow: 'auto', py: 1 }}>
          {filteredDocs.length === 0 ? (
            <Box sx={{ p: 3, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                No documents match "{searchQuery}"
              </Typography>
            </Box>
          ) : (
            <List disablePadding>
              {Array.from(groupedDocs.entries()).map(([category, docs]) => (
                <Box key={category}>
                  {/* Category Header */}
                  <ListItemButton
                    onClick={() => toggleCategory(category)}
                    sx={{
                      py: 0.75,
                      px: 2,
                      bgcolor: theme.palette.grey[100],
                    }}
                  >
                    <ListItemText
                      primary={category}
                     slotProps={{ primary: {
                        variant: 'overline',
                        color: 'text.secondary',
                        sx: { fontWeight: 600 },
                      } }}/>
                    {expandedCategories.has(category) ? (
                      <ExpandLessIcon fontSize="small" color="action" />
                    ) : (
                      <ExpandMoreIcon fontSize="small" color="action" />
                    )}
                  </ListItemButton>

                  <Collapse in={expandedCategories.has(category)}>
                    {docs.map(doc => {
                      const Icon = getIcon(doc.icon);
                      const isSelected = doc.id === selectedDocId;
                      const matchCount = countMatches(doc.content);

                      return (
                        <ListItemButton
                          key={doc.id}
                          selected={isSelected}
                          onClick={() => setSelectedDocId(doc.id)}
                          sx={{
                            py: 1.5,
                            px: 2,
                            '&.Mui-selected': {
                              bgcolor: alpha(theme.palette.primary.main, 0.08),
                              borderRight: `3px solid ${theme.palette.primary.main}`,
                              '&:hover': {
                                bgcolor: alpha(theme.palette.primary.main, 0.12),
                              },
                            },
                          }}
                        >
                          <ListItemIcon sx={{ minWidth: 36 }}>
                            <Icon
                              fontSize="small"
                              sx={{
                                color: isSelected
                                  ? theme.palette.primary.main
                                  : theme.palette.action.active,
                              }}
                            />
                          </ListItemIcon>
                          <ListItemText
                            primary={doc.title}
                            secondary={doc.description || undefined}
                           slotProps={{ primary: {
                              variant: 'body2',
                              color: isSelected ? 'primary.main' : 'text.primary',
                              sx: { fontWeight: isSelected ? 600 : 400 },
                            }, secondary: {
                              variant: 'caption',
                              noWrap: true,
                            } }}/>
                          {searchQuery && matchCount > 0 && (
                            <Chip
                              size="small"
                              label={matchCount}
                              sx={{
                                height: 20,
                                fontSize: '0.7rem',
                                bgcolor: alpha(theme.palette.warning.main, 0.15),
                                color: theme.palette.warning.dark,
                              }}
                            />
                          )}
                        </ListItemButton>
                      );
                    })}
                  </Collapse>
                </Box>
              ))}
            </List>
          )}
        </Box>
      </Box>

      {/* Main Content Area */}
      <Box sx={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        {selectedDoc ? (
          <>
            {/* Document Header */}
            <Box
              sx={{
                p: 3,
                borderBottom: `1px solid ${theme.palette.divider}`,
                bgcolor: 'white',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 48,
                    height: 48,
                    borderRadius: 2,
                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                  }}
                >
                  {React.createElement(getIcon(selectedDoc.icon), {
                    sx: { color: theme.palette.primary.main, fontSize: 28 },
                  })}
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
                    {selectedDoc.title}
                  </Typography>
                  {selectedDoc.description && (
                    <Typography variant="body2" color="text.secondary">
                      {selectedDoc.description}
                    </Typography>
                  )}
                  <Box sx={{ display: 'flex', gap: 1, mt: 1.5, flexWrap: 'wrap' }}>
                    <Chip
                      size="small"
                      label={selectedDoc.category}
                      sx={{ bgcolor: alpha(theme.palette.primary.main, 0.1) }}
                    />
                    {selectedDoc.tags.map(tag => (
                      <Chip
                        key={tag}
                        size="small"
                        icon={<TagIcon sx={{ fontSize: '14px !important' }} />}
                        label={tag}
                        variant="outlined"
                        sx={{ borderColor: theme.palette.grey[300] }}
                      />
                    ))}
                  </Box>
                </Box>
                <Tooltip title={`Source: ${selectedDoc.filename}`}>
                  <Typography variant="caption" color="text.disabled" sx={{ whiteSpace: 'nowrap' }}>
                    {new Date(selectedDoc.lastModified).toLocaleDateString()}
                  </Typography>
                </Tooltip>
              </Box>
            </Box>

            {/* Markdown Content */}
            <Box
              sx={{
                flex: 1,
                overflow: 'auto',
                p: 4,
                bgcolor: 'white',
                '& h1': {
                  fontSize: '1.75rem',
                  fontWeight: 700,
                  color: theme.palette.text.primary,
                  mb: 2,
                  pb: 1,
                  borderBottom: `2px solid ${theme.palette.primary.main}`,
                },
                '& h2': {
                  fontSize: '1.35rem',
                  fontWeight: 600,
                  color: theme.palette.text.primary,
                  mt: 4,
                  mb: 2,
                  pb: 0.5,
                  borderBottom: `1px solid ${theme.palette.divider}`,
                },
                '& h3': {
                  fontSize: '1.15rem',
                  fontWeight: 600,
                  color: theme.palette.text.primary,
                  mt: 3,
                  mb: 1.5,
                },
                '& h4': {
                  fontSize: '1rem',
                  fontWeight: 600,
                  color: theme.palette.primary.main,
                  mt: 2.5,
                  mb: 1,
                  fontFamily: 'monospace',
                  bgcolor: alpha(theme.palette.primary.main, 0.05),
                  px: 1.5,
                  py: 0.5,
                  borderRadius: 1,
                  display: 'inline-block',
                },
                '& p': {
                  color: theme.palette.text.secondary,
                  lineHeight: 1.7,
                  mb: 1.5,
                },
                '& strong': {
                  color: theme.palette.text.primary,
                  fontWeight: 600,
                },
                '& ul, & ol': {
                  pl: 3,
                  mb: 2,
                  '& li': {
                    color: theme.palette.text.secondary,
                    mb: 0.5,
                    lineHeight: 1.6,
                  },
                },
                '& code': {
                  fontFamily: 'monospace',
                  fontSize: '0.875em',
                  bgcolor: theme.palette.grey[100],
                  px: 0.75,
                  py: 0.25,
                  borderRadius: 0.5,
                  color: theme.palette.error.dark,
                },
                '& pre': {
                  bgcolor: theme.palette.grey[900],
                  color: theme.palette.grey[100],
                  p: 2,
                  borderRadius: 1,
                  overflow: 'auto',
                  mb: 2,
                  '& code': {
                    bgcolor: 'transparent',
                    color: 'inherit',
                    p: 0,
                  },
                },
                '& hr': {
                  border: 'none',
                  borderTop: `1px solid ${theme.palette.divider}`,
                  my: 3,
                },
                '& blockquote': {
                  borderLeft: `4px solid ${theme.palette.primary.main}`,
                  pl: 2,
                  py: 0.5,
                  my: 2,
                  bgcolor: alpha(theme.palette.primary.main, 0.05),
                  borderRadius: '0 8px 8px 0',
                  '& p': {
                    mb: 0,
                    fontStyle: 'italic',
                  },
                },
                '& table': {
                  width: '100%',
                  borderCollapse: 'collapse',
                  mb: 2,
                  '& th, & td': {
                    border: `1px solid ${theme.palette.divider}`,
                    px: 2,
                    py: 1,
                    textAlign: 'left',
                  },
                  '& th': {
                    bgcolor: theme.palette.grey[100],
                    fontWeight: 600,
                  },
                  '& tr:nth-of-type(even)': {
                    bgcolor: theme.palette.grey[50],
                  },
                },
                // Highlight search matches
                '& mark': {
                  bgcolor: alpha(theme.palette.warning.main, 0.3),
                  color: 'inherit',
                  px: 0.25,
                  borderRadius: 0.25,
                },
              }}
            >
              <ReactMarkdown>
                {searchQuery
                  ? highlightMatches(selectedDoc.content, searchQuery)
                  : selectedDoc.content}
              </ReactMarkdown>
            </Box>
          </>
        ) : (
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: theme.palette.grey[50],
            }}
          >
            <Typography color="text.secondary">Select a document to view</Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
}

export default AnalyticsDocs;
